import { useGetUploadDocuments } from "@/features/business_profile/useGetUploadDocuments";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Folder, LoaderCircle, Paperclip, User } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useGenerateOutline } from "./useGenerateOutline";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { PiMagicWandDuotone } from "react-icons/pi";
import { useGetAttachments } from "@/features/contract/useGetAttachments";
import { getAttachment } from "@/features/contract/Attachments";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { AnimatedShinyArray } from "@/components/ui/animated-shiny-array";
import {
  useGetOutlineAttachments,
  useGetOutlineDocuments,
} from "@/stores/ProposalStore";
import { OutlineFileUploadFormField } from "./OutlineFileUploadFormField";
import { useGetPursuitDocs } from "@/features/pursuit/useGetPursuitDocs";

const loadingTexts = [
  "This will take a few seconds...",
  "Processing attachments...",
  "Almost there...",
  "Analyzing opportunity...",
];

const FormSchema = z
  .object({
    documents: z.array(z.string()), // user documents
    attachments: z.array(z.string()), // opportunity attachments
    files: z // uploaded files
      .array(z.any()) // Ensure it handles multiple files
      .optional() // Make the whole field optional
      .refine(
        (files) =>
          !files || // Allow the field to be absent
          files.every((file) => {
            // Ensure that every file is either a PDF, DOCX, or DOC
            const fileType = file.type;
            return (
              fileType === "application/pdf" ||
              fileType ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              fileType === "application/msword"
            );
          }),
        {
          message: "Only pdf, docx, and doc files are allowed.",
        }
      ),
  })
  .superRefine((data, ctx) => {
    if (data.attachments?.length === 0 && data.files?.length === 0) {
      ctx.addIssue({
        path: ["attachments"],
        message: "You have to select at least one attachment or upload a file.",
      });
      ctx.addIssue({
        path: ["files"],
        message: "You have to select at least one attachment or upload a file.",
      });
    }
  });

function DocumentFolderViewForm({ form, documents }) {
  const formDocuments = form.watch("documents");

  const filteredData = documents?.filter((d) => d.type !== "Business Profile");

  const groupedDocuments = filteredData?.reduce((acc, doc) => {
    const folderName = doc.type || "Other";
    if (!acc[folderName]) {
      acc[folderName] = [];
    }
    acc[folderName].push(doc);
    return acc;
  }, {});
  const isShowDocuments =
    groupedDocuments && Object.keys(groupedDocuments)?.length > 0;
  return (
    <div className="border rounded-md p-4 h-full space-y-2 bg-[var(--gray-50)]">
      <div className="mb-4">
        <FormLabel className="text-base flex items-center gap-2">
          <span className="bg-muted/70 rounded-sm p-0.5">
            <User className="h-4 w-4 text-secondary-foreground" />
          </span>
          Your Documents
        </FormLabel>
      </div>
      {isShowDocuments ? (
        Object.entries(groupedDocuments).map(([folderName, docs]) => (
          <Accordion
            type="single"
            collapsible
            defaultValue={folderName}
            key={folderName}
          >
            <AccordionItem value={folderName}>
              <div key={folderName} className="">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={docs?.every((item) =>
                      formDocuments?.includes(item.file_id)
                    )}
                    onCheckedChange={(checked) => {
                      form.setValue(
                        "documents",
                        checked
                          ? // Add only the missing file_ids from docs
                            Array.from(
                              new Set([
                                ...formDocuments,
                                ...docs.map((item) => item.file_id),
                              ])
                            )
                          : // Remove only the file_ids that are in docs, keep others
                            formDocuments?.filter(
                              (value) =>
                                !docs.some((item) => item.file_id === value)
                            ) || []
                      );
                    }}
                  />
                  <Folder className="stroke-1 h-5 w-5 stroke-[var(--gray-300)] fill-[var(--sky-100)]" />
                  <AccordionTrigger className="flex text-start  gap-2">
                    {folderName}
                    <span className="text-muted-foreground">
                      ({docs.length})
                    </span>
                  </AccordionTrigger>
                </div>
                <AccordionContent>
                  <div className="ml-6 space-y-2 mt-2">
                    <FormField
                      control={form.control}
                      name="documents"
                      render={() => (
                        <FormItem>
                          {docs?.map((item) => (
                            <FormField
                              key={item.file_id}
                              control={form.control}
                              name="documents"
                              render={({ field }) => {
                                return (
                                  <FormItem
                                    key={item.file_id}
                                    className="flex flex-row items-start space-x-3 space-y-0"
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(
                                          item.file_id
                                        )}
                                        onCheckedChange={(checked) => {
                                          return checked
                                            ? field.onChange([
                                                ...field.value,
                                                item.file_id,
                                              ])
                                            : field.onChange(
                                                field.value?.filter(
                                                  (value) =>
                                                    value !== item.file_id
                                                )
                                              );
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      {item.title}
                                    </FormLabel>
                                  </FormItem>
                                );
                              }}
                            />
                          ))}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">
          No documents uploaded yet.{" "}
          <Button variant="link" className="p-0">
            <Link to="/business_profile">Upload here.</Link>
          </Button>
        </p>
      )}
    </div>
  );
}

function OutlineFileForm({
  searchType,
  proposal_id,
  attachments = [],
  documents = [],
  defaultDocuments = [],
  defaultAttachments = [],
  pursuitId,
}) {
  const { mutate: generateOutline, isLoading } = useGenerateOutline(
    searchType,
    proposal_id,
    pursuitId
  );
  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      documents: defaultDocuments,
      attachments: !pursuitId ? defaultAttachments : [],
    },
  });
  function onSubmit(data) {
    generateOutline(data);
  }
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={`${
          pursuitId
            ? "space-y-2 py-4"
            : "space-y-2 md:space-y-0 grid grid-cols-1 md:grid-cols-2 py-4 gap-4 items-start"
        }`}
      >
        {!pursuitId && (
          <FormField
            control={form.control}
            name="attachments"
            render={() => (
              <FormItem className="border rounded-md p-4 h-full bg-[var(--gray-50)]">
                <div className="mb-4">
                  <FormLabel className="text-base flex items-center gap-2 ">
                    <span className="bg-muted/70 rounded-sm p-0.5">
                      <Paperclip className="h-4 w-4 text-secondary-foreground" />
                    </span>
                    Attachments
                  </FormLabel>
                </div>
                {attachments?.map((item) => (
                  <FormField
                    key={item.attachmentId}
                    control={form.control}
                    name="attachments"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={item.attachmentId}
                          className="flex flex-row items-start space-x-3 space-y-0"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(item.attachmentId)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([
                                      ...field.value,
                                      item.attachmentId,
                                    ])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== item.attachmentId
                                      )
                                    );
                              }}
                              className="mt-1"
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">
                            {item.name}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <DocumentFolderViewForm form={form} documents={documents} />
        {!pursuitId && <OutlineFileUploadFormField form={form} />}

        <div className="md:col-span-2 font-semibold w-full flex justify-center">
          <Button
            className="max-w-md w-full mt-2 disabled:opacity-70"
            disabled={isLoading}
            type="submit"
            size="lg"
          >
            {isLoading ? (
              <>
                <AnimatedShinyArray
                  className={"text-sky-200/70 via-white"}
                  texts={loadingTexts}
                  renderText={(text) => (
                    <span className="flex items-center justify-center gap-2">
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      {text}
                    </span>
                  )}
                />
              </>
            ) : (
              <>
                <PiMagicWandDuotone size={20} />
                Generate Outline
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function OutlineFilePicker({ searchType, proposal_id, pursuitId }) {
  const { data: documents, isLoading: isDocumentsLoading } =
    useGetUploadDocuments();

  const { data: attachments, isLoading: isAttachmentsLoading } =
    useGetAttachments(searchType, proposal_id);
  const { data: pursuitDocs, isLoading: isLoadingPursuitDocs } =
    useGetPursuitDocs(pursuitId);

  const unifiedAttachments = attachments?.map((attachment) =>
    getAttachment(attachment, searchType)
  );
  const previouslyChosenDocuments = useGetOutlineDocuments(
    searchType,
    proposal_id
  );
  const previouslyChosenAttachments = useGetOutlineAttachments(
    searchType,
    proposal_id
  );
  if (isDocumentsLoading || isAttachmentsLoading || isLoadingPursuitDocs) {
    return (
      <div className="flex flex-col space-y-2 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="w-full h-24" />
          <Skeleton className="w-full h-24" />
          <Skeleton className="md:col-span-2 w-full h-24" />
        </div>
      </div>
    );
  }
  if (!pursuitDocs || (pursuitDocs && pursuitDocs.length === 0)) {
    return (
      <div className="flex flex-col space-y-2 w-full">
        <div className="text-center">
          No journey documents found. <br />
          <Link to={`../documents`} className="underline">
            Please upload at least one document.
          </Link>
        </div>
      </div>
    );
  }
  if (!documents || (documents && documents.length === 0)) {
    return (
      <div className="flex flex-col space-y-2 w-full">
        <div className="text-center">
          No company profile documents found. <br />
          To get results tailored to your business fill in your company profile
          info or upload documents. <br />
          <Link to={`/business_profile`} className="underline">
            Click here to upload
          </Link>
        </div>
      </div>
    );
  }
  const defaultAttachments = previouslyChosenAttachments || [
    ...(unifiedAttachments || [])?.map((file) => file.attachmentId),
  ];
  const defaultDocuments = previouslyChosenDocuments || [
    ...documents?.map((file) => file.file_id),
    ...pursuitDocs?.map((file) => file.openai_id),
  ];
  return (
    <div className="flex flex-col space-y-2 w-full">
      <OutlineFileForm
        searchType={searchType}
        proposal_id={proposal_id}
        attachments={unifiedAttachments}
        documents={documents}
        defaultAttachments={defaultAttachments}
        defaultDocuments={defaultDocuments}
        pursuitId={pursuitId}
      />
    </div>
  );
}

export { OutlineFilePicker, OutlineFileForm };
